<template>
  <Modal ref="modal" :can-close="!loading">
    <template #content>
      <h2 class="box__title">
        {{ $t('tableReportModal.title', { name: table.name }) }}
      </h2>
      <p>{{ $t('tableReportModal.description') }}</p>
      <Error :error="error"></Error>
      <Alert v-if="fieldsLoaded && dateFields.length === 0" type="warning">
        <template #title>{{ $t('tableReportModal.noDateFieldTitle') }}</template>
        <p>{{ $t('tableReportModal.noDateField') }}</p>
      </Alert>
      <div v-else>
        <FormGroup
          :label="$t('tableReportModal.period')"
          required
          class="margin-bottom-2"
        >
          <div class="table-report-modal__period">
            <input
              v-model="fromDate"
              type="date"
              class="input"
              :disabled="loading"
            />
            <span class="table-report-modal__period-separator">
              {{ $t('tableReportModal.periodTo') }}
            </span>
            <input
              v-model="toDate"
              type="date"
              class="input"
              :disabled="loading"
            />
          </div>
        </FormGroup>
        <FormGroup
          v-if="dateFields.length > 1"
          :label="$t('tableReportModal.dateField')"
          required
          class="margin-bottom-2"
        >
          <Dropdown v-model="dateFieldId" :disabled="loading">
            <DropdownItem
              v-for="field in dateFields"
              :key="field.id"
              :name="field.name"
              :value="field.id"
            ></DropdownItem>
          </Dropdown>
        </FormGroup>
        <FormGroup
          :label="$t('tableReportModal.format')"
          required
          class="margin-bottom-2"
        >
          <RadioGroup
            v-model="format"
            type="button"
            :options="formatOptions"
            :disabled="loading"
          ></RadioGroup>
        </FormGroup>
        <div class="align-right">
          <Button
            type="primary"
            size="large"
            :loading="loading"
            :disabled="loading || !fromDate || !toDate"
            @click="generate()"
          >
            {{ $t('tableReportModal.generate') }}
          </Button>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script>
import moment from '@baserow/modules/core/moment'
import modal from '@baserow/modules/core/mixins/modal'
import error from '@baserow/modules/core/mixins/error'
import TableService from '@baserow/modules/database/services/table'
import FieldService from '@baserow/modules/database/services/field'

export default {
  name: 'TableReportModal',
  mixins: [modal, error],
  props: {
    database: {
      type: Object,
      required: true,
    },
    table: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      fieldsLoaded: false,
      dateFields: [],
      dateFieldId: null,
      fromDate: moment().startOf('month').format('YYYY-MM-DD'),
      toDate: moment().format('YYYY-MM-DD'),
      format: 'pdf',
    }
  },
  computed: {
    formatOptions() {
      return [
        { label: this.$t('tableReportModal.pdf'), value: 'pdf' },
        { label: this.$t('tableReportModal.excel'), value: 'xlsx' },
      ]
    },
  },
  methods: {
    async show(...args) {
      modal.methods.show.bind(this)(...args)
      this.hideError()
      this.fieldsLoaded = false
      try {
        const { data } = await FieldService(this.$client).fetchAll(this.table.id)
        this.dateFields = data.filter((field) => field.type === 'date')
        this.dateFieldId = this.dateFields[0]?.id ?? null
      } catch (error) {
        this.handleError(error)
      }
      this.fieldsLoaded = true
    },
    async generate() {
      this.hideError()
      this.loading = true
      try {
        const response = await TableService(this.$client).report(this.table.id, {
          fromDate: this.fromDate,
          toDate: this.toDate,
          dateFieldId: this.dateFieldId,
          format: this.format,
        })
        const disposition = response.headers['content-disposition'] || ''
        const match = disposition.match(/filename="(.+?)"/)
        const filename =
          match?.[1] || `relatorio.${this.format === 'xlsx' ? 'xlsx' : 'pdf'}`
        const url = window.URL.createObjectURL(response.data)
        const link = document.createElement('a')
        link.href = url
        link.download = filename
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
        this.hide()
      } catch (error) {
        // Erros da API chegam como Blob por causa do responseType.
        if (error.response?.data instanceof Blob) {
          try {
            error.response.data = JSON.parse(await error.response.data.text())
          } catch {
            // mantém o blob se não for JSON
          }
        }
        this.handleError(error)
      }
      this.loading = false
    },
  },
}
</script>
